import React from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useMetricStore } from '../../store/metricStore';
import { DraggableMetric } from './DraggableMetric';

interface MetricTileManagerProps {
  onClose: () => void;
}

export function MetricTileManager({ onClose }: MetricTileManagerProps) {
  const { metrics, updateMetricOrder, toggleMetric, resetToDefault } = useMetricStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = metrics.findIndex((m) => m.id === active.id);
      const newIndex = metrics.findIndex((m) => m.id === over.id);
      const newMetrics = arrayMove(metrics, oldIndex, newIndex);
      updateMetricOrder(newMetrics.map((m, i) => ({ ...m, order: i })));
    }
  };

  const enabledCount = metrics.filter(m => m.enabled).length;

  return (
    <div className="space-y-4">
      {enabledCount >= 5 && (
        <div className="bg-warning/10 text-warning rounded-lg p-3 text-sm">
          Maximum 5 metrics can be shown at once
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={metrics}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {metrics
              .sort((a, b) => a.order - b.order)
              .map((metric) => (
                <DraggableMetric
                  key={metric.id}
                  id={metric.id}
                  metric={metric}
                  onToggle={() => toggleMetric(metric.id)}
                  disabled={!metric.enabled && enabledCount >= 5}
                />
              ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="flex justify-end gap-2">
        <button
          onClick={resetToDefault}
          className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground hover:bg-muted/70 transition-colors text-xs"
        >
          Reset
        </button>
        <button
          onClick={onClose}
          className="theme-button px-3 py-1.5 rounded-lg text-xs"
        >
          Done
        </button>
      </div>
    </div>
  );
}